import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar } from 'lucide-react';
import AdminSidebarNavigation from 'components/ui/AdminSidebarNavigation';
import ExportCategory from './components/ExportCategory';
import ExportPreview from './components/ExportPreview';
import ExportHistory from './components/ExportHistory';
import ScheduledExports from './components/ScheduledExports';
import PlayerSelectionModal from './components/PlayerSelectionModal';
import { exportService } from '../../services/exportService';

const DataExportDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('exports');
  const [selectedExport, setSelectedExport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [exportCategories, setExportCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingModalMessage, setLoadingModalMessage] = useState('');

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    // Redirect comms_user to Communications page
    if (storedRole === 'comms_user') {
      navigate('/admin-communication-center');
    } else {
      loadExportCategories();
    }
  }, [navigate]);

  // Loading Modal Component
  const LoadingModal = ({ isOpen, message, onClose }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex flex-col items-center text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Processing Export Request</h3>
            <p className="text-sm text-gray-600 mb-4">{message}</p>
            {message.includes('submitted') && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const loadExportCategories = async () => {
    try {
      setLoading(true);
      const baseCategories = [
        {
          id: 'draws',
          title: 'Draws Export',
          description: 'Serial Number, Team Name, Age Group',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '1-2 minutes',
          lastExported: null,
          fields: ['Serial Number', 'Team Name', 'Age Group']
        },
        {
          id: 'apparel',
          title: 'Apparel Export',
          description: 'Serial Number, Age Group, Player Name, School Name, T-Shirt Size, Pant Size',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '2-3 minutes',
          lastExported: null,
          fields: ['Serial Number', 'Age Group', 'Player Name', 'School Name', 'T-Shirt Size', 'Pant Size']
        },
        {
          id: 'player_details',
          title: 'Player Export',
          description:
            'Player Name, School Name, Age Group, Date of Birth, Age, Mobile Number, Email ID, T-Shirt Size, Pant Size',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '2-3 minutes',
          lastExported: null,
          fields: [
            'Player Name',
            'School Name',
            'Age Group',
            'Date of Birth',
            'Age',
            'Mobile Number',
            'Email ID',
            'T-Shirt Size',
            'Pant Size'
          ]
        },
        {
          id: 'player_cards',
          title: 'Player Cards Export',
          description: 'Bulk or selection-based export for player cards (designated by organizer)',
          icon: FileText,
          formats: ['PDF'],
          estimatedTime: '3-5 minutes',
          lastExported: null,
          fields: [
            'Player Name',
            'School Name',
            'Age Group',
            'Mobile Number',
            'Email ID',
            'T-Shirt Size',
            'Pant Size',
            'MCA ID',
            'Role',
            'Date of Birth',
            'Captain'
          ]
        }
      ];

      // Load last exported dates for each category
      const categoriesWithDates = await Promise.all(
        baseCategories.map(async (category) => {
          const lastExported = await exportService.getLastExportedDate(category.id);
          return {
            ...category,
            lastExported: lastExported || null
          };
        })
      );

      setExportCategories(categoriesWithDates);
    } catch (error) {
      console.error('Error loading export categories:', error);
      // Fallback to base categories if loading fails
      setExportCategories([
        {
          id: 'draws',
          title: 'Draws Export',
          description: 'Serial Number, Team Name, Age Group',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '1-2 minutes',
          lastExported: null,
          fields: ['Serial Number', 'Team Name', 'Age Group']
        },
        {
          id: 'apparel',
          title: 'Apparel Export',
          description: 'Serial Number, Age Group, Player Name, School Name, T-Shirt Size, Pant Size',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '2-3 minutes',
          lastExported: null,
          fields: ['Serial Number', 'Age Group', 'Player Name', 'School Name', 'T-Shirt Size', 'Pant Size']
        },
        {
          id: 'player_details',
          title: 'Player Export',
          description:
            'Player Name, School Name, Age Group, Date of Birth, Age, Mobile Number, Email ID, T-Shirt Size, Pant Size',
          icon: FileText,
          formats: ['Excel', 'CSV'], // PDF format hidden
          estimatedTime: '2-3 minutes',
          lastExported: null,
          fields: [
            'Player Name',
            'School Name',
            'Age Group',
            'Date of Birth',
            'Age',
            'Mobile Number',
            'Email ID',
            'T-Shirt Size',
            'Pant Size'
          ]
        },
        {
          id: 'player_cards',
          title: 'Player Cards Export',
          description: 'Bulk or selection-based export for player cards (designated by organizer)',
          icon: FileText,
          formats: ['PDF'],
          estimatedTime: '3-5 minutes',
          lastExported: null,
          fields: [
            'Player Name',
            'School Name',
            'Age Group',
            'Mobile Number',
            'Email ID',
            'T-Shirt Size',
            'Pant Size',
            'MCA ID',
            'Role',
            'Date of Birth',
            'Captain'
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportClick = (category) => {
    setSelectedExport(category);
    // For player_cards, show preview first to set filters, then player selection
    setShowPreview(true);
  };

  const handleConfirmExport = async (format, filters = {}) => {
    try {
      const exportType = selectedExport?.id;
      
      // Show loading modal immediately
      setShowLoadingModal(true);
      setLoadingModalMessage('Please wait, submitting export request...');
      
      // For player_cards, export directly without player selection modal
      if (exportType === 'player_cards') {
        // Request export asynchronously via Edge Function → Pub/Sub → Cloud Function
        const result = await exportService.requestExport(exportType, format, filters);
        
        if (result.success) {
          // Update modal message
          setLoadingModalMessage(`Request submitted successfully! Processing players from ${filters.schoolName || 'selected school'}. You can track its status in the Export History tab.`);
          
          // Subscribe to status updates
          const subscription = exportService.subscribeToExportStatus(result.exportHistoryId, (updatedRecord) => {
            console.log('Export status updated:', updatedRecord);
            
            if (updatedRecord.status === 'completed') {
              setShowLoadingModal(false);
              alert(`Player cards export completed! File: ${updatedRecord.file_name}`);
              subscription.unsubscribe();
            } else if (updatedRecord.status === 'failed') {
              setShowLoadingModal(false);
              alert(`Export failed: ${updatedRecord.error_message || 'Unknown error'}`);
              subscription.unsubscribe();
            }
          });
          
          // Auto-close modal after 3 seconds
          setTimeout(() => {
            setShowLoadingModal(false);
          }, 3000);
          
          await loadExportCategories();
          setShowPreview(false);
          setSelectedExport(null);
          return;
        } else {
          throw new Error(result.error || 'Failed to request export');
        }
      }

      // Request export asynchronously via Edge Function → Pub/Sub → Cloud Function
      const result = await exportService.requestExport(exportType, format, filters);
      
      if (result.success) {
        // Update modal message
        setLoadingModalMessage('Request submitted successfully! Your export is being processed. You can track its status in the Export History tab.');
        
        // Subscribe to status updates
        const subscription = exportService.subscribeToExportStatus(result.exportHistoryId, (updatedRecord) => {
          console.log('Export status updated:', updatedRecord);
          
          if (updatedRecord.status === 'completed') {
            setShowLoadingModal(false);
            alert(`Export completed! File: ${updatedRecord.file_name}`);
            subscription.unsubscribe();
          } else if (updatedRecord.status === 'failed') {
            setShowLoadingModal(false);
            alert(`Export failed: ${updatedRecord.error_message || 'Unknown error'}`);
            subscription.unsubscribe();
          }
        });
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowLoadingModal(false);
        }, 3000);
        
        // Reload categories to update last exported dates
        await loadExportCategories();
        
        setShowPreview(false);
        setSelectedExport(null);
      } else {
        throw new Error(result.error || 'Failed to request export');
      }
    } catch (error) {
      console.error('Error requesting export:', error);
      setShowLoadingModal(false);
      alert(`Failed to request export: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebarNavigation 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Data Export Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Generate reports and export registration data</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Hide Schedule Export button */}
              {false && (
                <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors touch-target w-full sm:w-auto">
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Schedule Export</span>
                  <span className="sm:hidden">Schedule</span>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 sm:gap-4 mt-4 border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('exports')}
              className={`pb-3 px-2 sm:px-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base touch-target ${
                activeTab === 'exports'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              Export Reports
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-3 px-2 sm:px-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base touch-target ${
                activeTab === 'history' ?'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              Export History
            </button>
            {/* Hide Scheduled Exports tab */}
            {false && (
              <button
                onClick={() => setActiveTab('scheduled')}
                className={`pb-3 px-2 sm:px-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base touch-target ${
                  activeTab === 'scheduled' ?'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Scheduled Exports
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {activeTab === 'exports' && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {exportCategories?.map(category => (
                    <ExportCategory
                      key={category?.id}
                      category={category}
                      onExport={handleExportClick}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'history' && <ExportHistory />}
          
          {/* Hide Scheduled Exports component */}
          {false && activeTab === 'scheduled' && <ScheduledExports />}
        </div>
      </div>
      {/* Export Preview Modal */}
      {showPreview && selectedExport && (
        <ExportPreview
          category={selectedExport}
          onClose={() => {
            setShowPreview(false);
            setSelectedExport(null);
          }}
          onConfirm={handleConfirmExport}
        />
      )}

      {/* Player Selection Modal for Player Cards Export */}
      {showPlayerSelection && selectedExport && (
        <PlayerSelectionModal
          isOpen={showPlayerSelection}
          onClose={() => {
            setShowPlayerSelection(false);
            setSelectedExport(null);
            setSelectedFilters({});
          }}
          onConfirm={async (selectedPlayerIds) => {
            try {
              const filters = selectedFilters;
              const format = 'PDF'; // Player cards are PDF only
              
              // Request export asynchronously
              const result = await exportService.requestExport(
                'player_cards', 
                format, 
                filters, 
                selectedPlayerIds
              );
              
              if (result.success) {
                alert(`Player cards export request submitted! Processing ${selectedPlayerIds.length} players. You can track its status in the Export History tab.`);
                
                // Subscribe to status updates
                const subscription = exportService.subscribeToExportStatus(result.exportHistoryId, (updatedRecord) => {
                  console.log('Export status updated:', updatedRecord);
                  
                  if (updatedRecord.status === 'completed') {
                    alert(`Player cards export completed! File: ${updatedRecord.file_name}`);
                    subscription.unsubscribe();
                  } else if (updatedRecord.status === 'failed') {
                    alert(`Export failed: ${updatedRecord.error_message || 'Unknown error'}`);
                    subscription.unsubscribe();
                  }
                });
                
                // Reload categories to update last exported dates
                await loadExportCategories();
                
                setShowPlayerSelection(false);
                setSelectedExport(null);
                setSelectedFilters({});
              } else {
                throw new Error(result.error || 'Failed to request export');
              }
            } catch (error) {
              console.error('Error requesting player cards export:', error);
              alert(`Failed to request export: ${error.message}`);
            }
          }}
          filters={selectedFilters}
        />
      )}

      {/* Loading Modal */}
      <LoadingModal 
        isOpen={showLoadingModal}
        message={loadingModalMessage}
        onClose={() => setShowLoadingModal(false)}
      />
    </div>
  );
};

export default DataExportDashboard;